import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const POLL_MS = 5000;
const RESOLVE_TRIES = 45;
const RESOLVE_INTERVAL_MS = 1500;

export function pickRunAfterDispatch(list, sinceMs, opts) {
  const slack = opts?.slackMs ?? 15000;
  const runs = list?.workflow_runs ?? [];
  const cutoff = sinceMs - slack;
  for (const r of runs) {
    if (opts?.branch && r.head_branch && r.head_branch !== opts.branch) continue;
    const t = r.created_at ? Date.parse(r.created_at) : 0;
    if (t >= cutoff) return r.id;
  }
  return null;
}

/**
 * Track a workflow run after dispatch: poll Desk GET /api/github/runs/{id} only (via local proxy).
 *
 * @param {string} proxyBaseUrl
 * @param {{ owner?: string|null, repo?: string|null }} repoCtx
 */
export function useWorkflowRunTracking(proxyBaseUrl, repoCtx = {}) {
  const owner = repoCtx.owner || undefined;
  const repo = repoCtx.repo || undefined;

  const [state, setState] = useState({
    phase: 'idle',
    runId: null,
    run: null,
  });
  const [error, setError] = useState(null);
  const pollRef = useRef(null);
  const mounted = useRef(true);
  const finishRef = useRef(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const settle = useCallback((err, payload) => {
    const fin = finishRef.current;
    finishRef.current = null;
    if (!fin) return;
    if (err) fin.reject(err);
    else fin.resolve(payload);
  }, []);

  const dispatchAndTrack = useCallback(
    async (body) => {
      stopPolling();
      setError(null);
      setState({ phase: 'dispatching', runId: null, run: null });

      const p = new Promise((resolve, reject) => {
        finishRef.current = { resolve, reject };
      });

      const startedAt = Date.now();
      let pollStopped = false;

      const fail = (msg) => {
        pollStopped = true;
        if (!mounted.current) return;
        stopPolling();
        setError(msg);
        setState({ phase: 'error', runId: null, run: null });
        settle(new Error(msg));
      };

      const repoParams = {};
      if (owner) repoParams.owner = owner;
      if (repo) repoParams.repo = repo;

      try {
        const res = await axios.post(
          `${proxyBaseUrl}/api/desk/dispatch-workflow`,
          {
            workflowId: body.workflowId,
            ref: body.ref,
            inputs: body.inputs || {},
          },
          { validateStatus: () => true, timeout: 120000 },
        );

        if (!mounted.current) return p;

        if (res.status < 200 || res.status >= 300) {
          const d = res.data || {};
          const msg = d.error || d.message || `Dispatch failed (HTTP ${res.status})`;
          fail(typeof msg === 'string' ? msg : JSON.stringify(msg));
          return p;
        }

        const data = res.data || {};
        let runId =
          data.workflow_run_id != null
            ? Number(data.workflow_run_id)
            : data.workflowRunId != null
              ? Number(data.workflowRunId)
              : null;

        if (runId != null && !Number.isNaN(runId)) {
          setState((s) => ({ ...s, phase: 'resolving_run', runId }));
        } else {
          setState((s) => ({ ...s, phase: 'resolving_run' }));
          const wfEnc = encodeURIComponent(String(body.workflowId));
          for (let i = 0; i < RESOLVE_TRIES && mounted.current; i++) {
            const listRes = await axios.get(
              `${proxyBaseUrl}/api/desk/github/workflows/${wfEnc}/runs`,
              {
                params: {
                  ...repoParams,
                  branch: body.ref,
                  event: 'workflow_dispatch',
                  per_page: 15,
                },
                timeout: 60000,
                validateStatus: () => true,
              },
            );
            if (listRes.status >= 200 && listRes.status < 300 && listRes.data) {
              runId = pickRunAfterDispatch(listRes.data, startedAt, { branch: body.ref });
            }
            if (runId != null) break;
            await new Promise((r) => setTimeout(r, RESOLVE_INTERVAL_MS));
          }
        }

        if (!mounted.current) return p;

        if (runId == null) {
          fail(
            'Workflow was dispatched but the new run was not found yet. Open Actions on GitHub or try again.',
          );
          return p;
        }

        setState({ phase: 'polling', runId, run: null });

        /** @returns {Promise<boolean>} */
        const tick = async () => {
          if (pollStopped || !mounted.current) return false;
          try {
            const runRes = await axios.get(`${proxyBaseUrl}/api/desk/github/runs/${runId}`, {
              params: {
                ...repoParams,
                exclude_pull_requests: true,
                excludePullRequests: true,
              },
              timeout: 60000,
              validateStatus: () => true,
            });

            if (!mounted.current) return false;

            if (runRes.status === 404) return true;

            if (runRes.status < 200 || runRes.status >= 300) {
              const d = runRes.data || {};
              const msg = d.error || d.message || `Run fetch HTTP ${runRes.status}`;
              fail(typeof msg === 'string' ? msg : JSON.stringify(msg));
              return false;
            }

            const run = runRes.data;
            setState({ phase: 'polling', runId, run });

            const terminal =
              run.status === 'completed' ||
              run.status === 'cancelled' ||
              run.conclusion === 'failure' ||
              run.conclusion === 'cancelled' ||
              run.conclusion === 'skipped';

            if (terminal) {
              pollStopped = true;
              stopPolling();
              setState({ phase: 'complete', runId, run });
              settle(null, { run });
              return false;
            }
            return true;
          } catch (e) {
            if (!mounted.current) return false;
            pollStopped = true;
            stopPolling();
            const msg = e instanceof Error ? e.message : 'Polling failed';
            setError(msg);
            setState((s) => ({ ...s, phase: 'error' }));
            settle(e instanceof Error ? e : new Error(msg));
            return false;
          }
        };

        const shouldContinue = await tick();
        if (shouldContinue && mounted.current && !pollStopped) {
          pollRef.current = setInterval(() => {
            void (async () => {
              const more = await tick();
              if (!more) stopPolling();
            })();
          }, POLL_MS);
        }
      } catch (e) {
        if (!mounted.current) return p;
        const msg = e instanceof Error ? e.message : 'Dispatch failed';
        fail(msg);
      }

      return p;
    },
    [proxyBaseUrl, owner, repo, settle, stopPolling],
  );

  const reset = useCallback(() => {
    stopPolling();
    finishRef.current = null;
    setError(null);
    setState({ phase: 'idle', runId: null, run: null });
  }, [stopPolling]);

  return { state, error, dispatchAndTrack, reset, stopPolling };
}
